import { createContext, useContext, useEffect, useState } from "react";

const translations = {
  es: {
    nav: { home: "Inicio", dashboard: "Panel", parents: "Padres", login: "Entrar", logout: "Salir" },
    hero: {
      kicker: "App educativa para niños de 5 a 12 años",
      title: "Convierte",
      titleIdeas: "tus ideas",
      titleIn: "en",
      titleAccent: "superpoderes",
      sub1: "Lumi te da ideas y Spark te guía con inteligencia artificial.",
      sub2: "Aprende a crear negocios, apps, páginas web y videojuegos, tomar fotos profesionales y aprender nuevos idiomas.",
      ctaStart: "¡Empecemos!",
      ctaParents: "Aprende y crea junto a tus hijos",
    },
    meetMascots: "Conoce a nuestros amigos",
    lumi: { name: "Lumi", role: "La bombilla de ideas" },
    spark: { name: "Spark", role: "El robot de IA" },
    modulesTitle: "8 mundos para explorar",
    learnA: "¿Qué",
    learnB: "aprenderá",
    learnC: "tu hijo?",
    learnSub: "Habilidades para imaginar, crear y prepararse para el futuro",
    modulesSub: "Cada mundo tiene lecciones cortas, retos y recompensas.",
    dashboard: {
      hello: "¡Hola",
      todaysMission: "Misión de hoy",
      streak: "Racha",
      coins: "Monedas",
      startChat: "Hablar con",
      continue: "Continuar",
      explore: "Explorar",
      lessons: "lecciones",
    },
    chat: {
      placeholder: "Escribe tu pregunta...",
      send: "Enviar",
      withLumi: "Chat con Lumi",
      withSpark: "Chat con Spark",
      startersLumi: ["Dame 3 ideas de negocio para niños", "¿Qué puedo vender en el recreo?", "Ideas para un canal de YouTube"],
      startersSpark: ["¿Qué es la inteligencia artificial?", "¿Cómo aprende una IA?", "¿Puedo crear mi propia IA?"],
    },
    lesson: { markDone: "Marcar como hecha", done: "¡Hecha! +10 monedas" },
    parents: {
      title: "Panel para familias",
      sub: "Una app para toda la familia: aprende tú también, descubre lo que tu hijo aprenderá y mira su progreso.",
      coinsEarned: "Monedas ganadas",
      lessonsDone: "Lecciones completadas",
      progress: "Progreso por mundo",
      tipsTitle: "Consejos para padres",
      tips: [
        "Acompaña 15 minutos al día: sé el público al que tu niño le enseña lo aprendido.",
        "Celebra el esfuerzo, no solo los resultados. 'Qué constancia' > 'qué listo'.",
        "Convierte lo digital en real: una foto se imprime, una idea se dibuja.",
        "Hablen de la IA: pregúntale '¿cómo sabes eso?' para despertar su pensamiento crítico.",
      ],
    },
    auth: { signIn: "Entrar", signUp: "Crear cuenta", unavailable: "El inicio de sesión no está disponible ahora. Recarga la página." },
    saveProfile: "Guardar",
    profile: { title: "Mi perfil", name: "Nombre", age: "Edad", language: "Idioma" },
  },
  en: {
    nav: { home: "Home", dashboard: "Dashboard", parents: "Parents", login: "Sign in", logout: "Sign out" },
    hero: {
      kicker: "Educational app for kids 5-12",
      title: "Turn",
      titleIdeas: "your ideas",
      titleIn: "into",
      titleAccent: "superpowers",
      sub1: "Lumi gives you ideas and Spark guides you with artificial intelligence.",
      sub2: "Learn to build businesses, apps, websites and games, take pro photos and learn new languages.",
      ctaStart: "Let's go!",
      ctaParents: "Learn and create with your kids",
    },
    meetMascots: "Meet our friends",
    lumi: { name: "Lumi", role: "The idea lightbulb" },
    spark: { name: "Spark", role: "The AI robot" },
    modulesTitle: "8 worlds to explore",
    learnA: "What will your child",
    learnB: "learn",
    learnC: "?",
    learnSub: "Skills to imagine, create and get ready for the future",
    modulesSub: "Each world has short lessons, challenges and rewards.",
    dashboard: {
      hello: "Hi",
      todaysMission: "Today's mission",
      streak: "Streak",
      coins: "Coins",
      startChat: "Chat with",
      continue: "Continue",
      explore: "Explore",
      lessons: "lessons",
    },
    chat: {
      placeholder: "Type your question...",
      send: "Send",
      withLumi: "Chat with Lumi",
      withSpark: "Chat with Spark",
      startersLumi: ["Give me 3 business ideas for kids", "What can I sell at recess?", "Ideas for a YouTube channel"],
      startersSpark: ["What is artificial intelligence?", "How does an AI learn?", "Can I build my own AI?"],
    },
    lesson: { markDone: "Mark as done", done: "Done! +10 coins" },
    parents: {
      title: "Family dashboard",
      sub: "An app for the whole family: learn too, discover what your child will learn and see their progress.",
      coinsEarned: "Coins earned",
      lessonsDone: "Lessons completed",
      progress: "Progress by world",
      tipsTitle: "Tips for parents",
      tips: [
        "Spend 15 minutes a day: be the audience your child teaches what they learned.",
        "Celebrate effort, not just results. 'What persistence' beats 'how smart'.",
        "Make digital real: print a photo, draw an idea.",
        "Talk about AI: ask 'how do you know that?' to spark critical thinking.",
      ],
    },
    auth: { signIn: "Sign in", signUp: "Sign up", unavailable: "Sign-in is unavailable right now. Refresh the page." },
    saveProfile: "Save",
    profile: { title: "My profile", name: "Name", age: "Age", language: "Language" },
  },
};

const I18nContext = createContext({ lang: "es", t: translations.es, setLang: () => {} });

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("lumi_lang") || "es");
  useEffect(() => {
    localStorage.setItem("lumi_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);
  const value = { lang, setLang, t: translations[lang] };
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useT = () => useContext(I18nContext);
